import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Modal from 'components/Modal';
import styled from 'styled-components';
import image_redeem from './redeem.png';
import Button from 'components/Button';
import { cashierBtnMultiPayV3 } from 'features/cashier/components/TickerFooter/ButtonMultiPayV3';
import { IBillDetailData } from 'features/payment/services/types/bill';
import shopSelectors from 'services/shop/selectors';
import { formatCurrency, formatNumber } from 'utils/formatCurrency';

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  .modal-title {
    color: #1D2129;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-align: center;
    margin-bottom: 24px;
    b {
      color: #D2464F;
      font-size: 22px;
      text-transform: capitalize;
    }
  }

  .image-redeem {
    width: 180px;
    height: 180px;
    object-fit: cover;
    margin-bottom: 16px;
  }

  .action-item {
    display: flex;
    padding: 24px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    border-radius: 8px;
    border: 1px solid #86909C;
    background: #FFF;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #D6D6E7 inset;

    span {
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    &:hover {
      background: #F5F5F5;
      cursor: pointer;
    }
  }

  .action-item img.icon {
    width: 64px;
    height: 64px;
  }

  .content-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    padding: 0px 24px;
    padding-top: 24px;

    .actions {
      display: flex;
      align-items: center;
      gap: 16px;
      align-self: stretch;
      padding: 32px 0;
    }

  }
`;
export type ModalCheckValidPointRef = {
  open: (detail: IBillDetailData, callback: () => void) => void;
};
type Props = {}
const ModalCheckValidPoint = forwardRef<ModalCheckValidPointRef, Props>(({ }, ref) => {
  const loyaltySetting = shopSelectors.data.getLoyaltySetting();
  const [open, setOpen] = useState(false);
  const callbackRef = useRef<(() => void) | null>(null);
  const [detail, setDetail] = useState<IBillDetailData | null>(null);
  const loyaltyRedeemConfig = shopSelectors.data.loyaltyRedeemConfig();
  const allSetting = shopSelectors.data.allSetting();

  const exchangeDollar = (point: number) => {
    if (!loyaltySetting) return 0;
    const val = +(point) / loyaltySetting?.oneDollarToPoint;
    return val;
  };
  const amount = useMemo(() => {
    const customerAmount = exchangeDollar(detail?.customerPoint || 0) || 0;
    const maxAmount = loyaltyRedeemConfig?.maxAmount || 0;
    return Math.min(customerAmount, maxAmount);
  }, [detail, loyaltyRedeemConfig]);

  const onClose = () => setOpen(false);
  const onOpen: ModalCheckValidPointRef['open'] = (detail, _callback) => {
    if ((detail?.customerPoint || 0) < (loyaltyRedeemConfig?.point || 0)) return _callback?.();
    if (!allSetting?.autoConfirmPayLoyalty) return _callback?.();
    setDetail(detail);
    setOpen(true);
    callbackRef.current = _callback || (() => undefined);
  };

  const onChoose = (type: 'NO' | 'YES') => {
    if (!detail) return;
    switch (type) {
      case 'NO':
        callbackRef.current?.();
        break;
      case 'YES': {
        const loyaltyPointVal = {
          amount,
          point: detail?.customerPoint || 0,
        };
        cashierBtnMultiPayV3.current?.continueLoyaltyPayWithMultiPayFetchDetail(detail, loyaltyPointVal);
        break;
      }

      default:
        break;
    }
    onClose();
  };

  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  return (
    <Modal hiddenHeader hiddenFooter visible={open} onClose={onClose} width={700} noneBodyStyle noneFooterStyle>
      <ContainerStyled>
        <div className='content-container'>
          <img src={image_redeem} alt='image-redeem' className='image-redeem' />
          <p className='modal-title'>You have <b>{formatNumber(detail?.customerPoint)}</b> loyalty points and you can redeem it to <b>{formatCurrency(amount)}</b> to pay for this bill or using for next bill</p>
          <div className='actions'>
            <Button block ntype='SECONDARY' cardCashier onClick={() => onChoose('NO')}><span style={{ fontSize: 20 }}>NO</span></Button>
            <Button block ntype='PRIMARY' cardCashier onClick={() => onChoose('YES')}><span style={{ fontSize: 20, color: '#fff' }}>YES</span></Button>
          </div>
        </div>
      </ContainerStyled>
    </Modal>
  );
});
ModalCheckValidPoint.displayName = 'ModalCheckValidPoint';

const useCheckValidPoint = (): [ModalCheckValidPointRef['open'], React.ReactNode] => {
  const ref = useRef<ModalCheckValidPointRef>(null);
  const context = <ModalCheckValidPoint ref={ref} />;
  const open: ModalCheckValidPointRef['open'] = (detail, callback) => {
    ref.current?.open(detail, callback);
  };
  return [
    open,
    context,
  ];
};

export default useCheckValidPoint;
